import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Table } from '@components/common';
import { Download } from '@components/svg';
import { AlertError, LoaderWrapper } from '@components/widgets';
import { useGetCurrentUser } from '@hooks/index';
import { SettingsTabsIDs } from '@pages/settings/Settings';
import DateInstance from '@services/date/date';
import { createCenteredPopup } from '@services/helper/helper';
import useBillingData from '@shared/hooks/payment/useBillingData';
import useKonnect from '@shared/hooks/payment/useKonnect';
import usePaymentInvoices from '@shared/hooks/payment/usePaymentInvoices';
import usePreventWindowClose from '@shared/hooks/payment/usePreventWindowClose';
import { GetPaymentOrdersOptions } from '@shared/interfaces/backoffice';

import './profileBillingHistory.scss';

const OrderToSortingMapper = {
    Invoices: 'id',
    Status: 'status',
    Amount: 'amount',
    Date: 'createdAt',
};

const statusToStatusDisplay = {
    unpaid: 'unpaid',
    confirmed: 'paid',
    pending: 'pending',
    cancelled: 'cancelled',
};

const ProfileBillingHistory = () => {
    const [, setSearchParams]                   = useSearchParams({ tab: SettingsTabsIDs.billingHistory });
    const [limit, setLimit]                     = useState(10);
    const [page, setPage]                       = useState(1);
    const [
        sort,
        setSort
    ]                                           = useState<{ with: GetPaymentOrdersOptions['sortBy']; reversed: boolean }>({
        with: 'id',
        reversed: false
    });
    const [paymentError, setPaymentError]       = useState(null);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const {
        hasError,
        isLoading,
        setIsLoading,
        initPayment,
    }                                           = useKonnect();
    const {
        isLoading: billingIsLoading,
        billingData,
        hasValidBillingData
    }                                           = useBillingData();
    const { triggerClosePrevent }               = usePreventWindowClose();
    const user                                  = useGetCurrentUser();
    const {
        isLoading: loadingInvoices,
        // hasError: invoicesError,
        fetchInvoices,
        invoices,
        downloadInvoice,
    }                                           = usePaymentInvoices({
        fetchOnInit: true,
        limit,
        page,
        sortBy: sort.with,
        sortOrder: sort.reversed ? 'DESC' : 'ASC'
    });
    const interval                              = useRef(null);

    const [pdfUrl, setPdfUrl] = useState(null);


    useEffect(() => {
        () => {
            if (pdfUrl) {
                URL.revokeObjectURL(pdfUrl);
            }
        };
    }, []);


    const handlePayment = (amount: number, date: string, plan: number, orderId: number) => () => {
        if (isLoading) {
            return;
        }

        triggerClosePrevent(true);

        initPayment({
            amount: Number(amount)*100,
            email: billingData?.email ?? user.email,
            firstName: user.firstname,
            lastName: user.lastname,
            phoneNumber: '-',
            webhook: `${window.location.origin}/payment-feedback?planId=${plan}&orderId=${orderId}`,
            description: `Type: Monthly payment | Date: ${date} | Site: ${user.site.website}`,
        })
            .then(result => {
                const paymentWindow = createCenteredPopup({
                    url: result.payUrl,
                    title: 'Indepth Payment',
                    w: 700,
                    h: 800,
                });

                triggerClosePrevent(true, paymentWindow);

                // watch for popup window closing
                interval.current = setInterval(async () => {
                    if (paymentWindow.closed) {
                        clearInterval(interval.current);
                        setIsLoading(false);

                        fetchInvoices();

                        return;
                    }
                }, 500);
            })
            .catch(e => {
                console.log(e);
            });
    };

    const handleDownloadInvoice = (orderReference: string) => () => {
        setDownloadLoading(true);
        downloadInvoice(orderReference)
            .then(result => {
                const blob = result as Blob;
                const file = new Blob([blob], { type: 'application/pdf' });
                const url  = URL.createObjectURL(file);

                setPdfUrl(url);
                createCenteredPopup({
                    url,
                    title: 'Payment Details',
                    w: 1200,
                    h: 800
                });
            })
            .finally(() => setDownloadLoading(false));
    };

    const bodyItems = useMemo(() => invoices?.data?.length ? (
        invoices.data.map((item) => {
            const invoiceDate = DateInstance.formatDate({
                date: DateInstance.newDate(item.endDate),
                format: 'dd MMM yyyy, hh:mm'
            });
            const amount      = Number(item.price);
            const status      = statusToStatusDisplay?.[item.status] ?? 'unpaid';

            const isDownload = ['pending', 'confirmed'].includes(status.toLowerCase());

            return [
                {
                    title: item.id,
                    titleBold: true,
                },
                status,
                String(amount),
                <div>
                    <p style={{ fontSize: '.7rem' }}>{invoiceDate}</p>

                    <div
                        onClick={isDownload ? handleDownloadInvoice(item.orderReference) : undefined}
                        style={{
                            cursor: 'pointer'
                        }}
                    >
                        {isDownload ? (
                            downloadLoading ? <LoaderWrapper /> : <Download />
                        ) : (
                            status.toLowerCase() === 'cancelled' ? null : (
                                <button
                                    disabled={isLoading}
                                    className='pay-button'
                                    onClick={handlePayment(amount, invoiceDate, item.planId, item.id)}
                                >
                                    {isLoading ? <LoaderWrapper /> : <p style={{ fontSize: '.7rem' }}>Pay Now</p>}
                                </button>
                            )
                        )}
                    </div>
                </div>
            ];
        })
    ) : [], [invoices, isLoading, downloadLoading]);

    return (
        <div className='profile-invoices'>
            {isLoading || loadingInvoices || billingIsLoading && <LoaderWrapper />}
            <div className='pi-header'>
                <h2>Invoices</h2>
                <p>See history of your payment plan.</p>
            </div>

            {hasError && <AlertError
                message={paymentError || 'An error occurred while processing your payment, please try again in a few minutes!'}
            />
            }

            <div className="pi-table">
                {!hasValidBillingData(billingData) ? (
                    <AlertError
                        message="Please fill in your billing data to get started!"
                        link="Go to billing"
                        linkClick={() => setSearchParams({ tab: SettingsTabsIDs.billing })}
                    />
                ) : (
                    <Table
                        onOrderChange={(orderKey: string, isOrderReversed: boolean) => {
                            setSort({ with: OrderToSortingMapper?.[orderKey], reversed: isOrderReversed });
                            setPage(1);
                        }}
                        defaultOrder={1}

                        nbPages={invoices?.pagination?.totalPages}
                        onCurrentPageChange={(currentPage) => setPage(currentPage)}
                        setNbResults={(value) => {
                            setLimit(value);
                            setPage(1);
                        }}

                        disabledDownloader
                        itemsNotBold
                        goToLinkWhenClickedOnTdFirstChild
                        headerItems={Object.keys(OrderToSortingMapper)}
                        bodyItems={bodyItems}

                        emptyBodyItemsMessage={'No Results'}
                    />
                )}
            </div>
        </div>
    );
};


export default ProfileBillingHistory;
